/* eslint-disable @typescript-eslint/camelcase */
/* eslint-disable no-bitwise */
/* eslint-disable no-restricted-globals */
import * as React from 'react'
import { Helmet } from 'react-helmet'

interface Utms {
  utm_cid?: string
  utm_source?: string
  utm_campaign?: string
  utm_medium?: string
  utm_term?: string
  utm_content?: string
}

type HitType =
  | 'pageview'
  | 'screenview'
  | 'event'
  | 'transaction'
  | 'item'
  | 'social'
  | 'exception'
  | 'timing'

interface AnalyticsContextProps {
  tid: string
  cid: string
  utms: Utms
  send: (t: HitType, param1?, param2?, param3?) => void
  onRouteUpdate: ({ location: Location }) => void
  onEvent: (event: { category: string; action: string; label: string }) => void
}

const AnalyticsContext = React.createContext<AnalyticsContextProps>(null!)

export function useAnalytics() {
  return React.useContext(AnalyticsContext) as AnalyticsContextProps
}

interface AnalyticsProviderOptions {
  tid: string
  pageTransitionDelay?: number
}

export const AnalyticsProvider: React.FC<AnalyticsProviderOptions> = ({
  tid,
  pageTransitionDelay,
  children
}) => {
  const utms = React.useRef<Utms>({})
  const cid = React.useRef<string>(null)

  const send = (t: HitType, ...rest) =>
    ga({ tid, cid: cid.current, utms: utms.current }, t, ...rest)

  const onEvent = ({
    category,
    action,
    label
  }: {
    category: string
    action: string
    label: string
  }) => {
    send('event', category, action, label)
  }

  const sendPageView = (location: Location) => {
    assureSessionData()
    processUTMs((href, e) => {
      send('event', 'outbound', 'click', href.replace(/^https?:\/\//i, ''))
      return true
    })
    send('pageview')
  }

  const onRouteUpdate = ({ location }: { location: Location }) => {
    // wrap inside a timeout to make sure react-helmet is done with it's changes (https://github.com/gatsbyjs/gatsby/issues/9139)
    // reactHelmet is using requestAnimationFrame: https://github.com/nfl/react-helmet/blob/5.2.0/src/HelmetUtils.js#L296-L299
    // Minimum delay for reactHelmet's requestAnimationFrame
    const delay = Math.max(32, pageTransitionDelay || 0)
    setTimeout(() => sendPageView(location), delay)
  }

  const assureSessionData = () => {
    let dirty = false
    const urlUtms = getQueryVariablesStartingWith('utm_')

    if (Object.keys(urlUtms).length > 0) {
      utms.current = urlUtms
      removeUtms()
      dirty = true
    }

    if (!utms.current) {
      if (sessionStorage) {
        const shc = sessionStorage.getItem('bsa_')
        if (shc) {
          utms.current = JSON.parse(shc)
        }
      }
    }

    if (!cid.current) {
      cid.current = utms.current.utm_cid

      if (!cid.current) {
        dirty = true
        if (localStorage) {
          cid.current = localStorage.getItem('bscid_')
        }
        if (!cid.current) {
          cid.current = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
            /[xy]/g,
            (c) => {
              const r = (Math.random() * 16) | 0
              const v = c === 'x' ? r : (r & 0x3) | 0x8
              return v.toString(16)
            }
          )
        }
        if (localStorage) {
          localStorage.setItem('bscid_', cid.current)
        }
      }
    }

    utms.current.utm_cid = cid.current

    if (dirty) {
      if (sessionStorage) {
        sessionStorage.setItem('bsa_', JSON.stringify(utms))
      }
    }
  }

  //
  // Build React Context Record
  //

  const contextValue: AnalyticsContextProps = React.useMemo(
    () => ({
      tid,
      cid: cid.current,
      utms: utms.current,
      send,
      onEvent,
      onRouteUpdate
    }),
    [tid, cid.current, utms.current]
  )

  return (
    <AnalyticsContext.Provider value={contextValue}>
      <Helmet>
        <link
          rel="preconnect dns-prefetch"
          key="preconnect-google-analytics"
          href="https://www.google-analytics.com"
        />
      </Helmet>
      {children}
    </AnalyticsContext.Provider>
  )
}

function ga(
  { tid, cid, utms }: Partial<AnalyticsContextProps>,
  t: HitType,
  param1?,
  param2?,
  param3?
) {
  const pagePath = location
    ? location.pathname + location.search + location.hash
    : undefined

  const fields = [
    ['v', 1], // protocol version
    ['t', t], // 'pageview', 'screenview', 'event', 'transaction', 'item', 'social', 'exception', 'timing'
    ['tid', tid], // GA id
    ['cid', cid], // SH id
    ['aip', 1], // anonymyze IP
    // ['uip', '0.0.0.0'],
    ['cn', utms.utm_campaign],
    ['cs', utms.utm_source],
    ['cm', utms.utm_medium],
    ['ck', utms.utm_term],
    ['cc', utms.utm_content],
    ['z', Math.floor(Math.random() * 10e7)] // Cache Buster
  ].filter((field) => !!field[1])

  if (t === 'pageview') {
    fields.push(['dl', pagePath])
  } else if (t === 'event') {
    fields.push(['ec', param1], ['ea', param2], ['el', param3])
  } else if (t === 'exception') {
    fields.push(['dt', param1], ['exd', param2])
  } else {
    throw new Error('unknown analytics type')
  }

  const payload = fields
    .map((el) => {
      return `${String(el[0])}=${encodeURIComponent(String(el[1]))}`
    })
    .join('&')

  const url = `https://www.google-analytics.com/r/collect`

  if (navigator && navigator.sendBeacon) {
    navigator.sendBeacon(url, payload)
  } else {
    const request = new XMLHttpRequest()
    request.open('POST', url, false)
    request.send(payload)
  }
}

function getQueryVariablesStartingWith(variable) {
  const query = location.search.substring(1)
  const vars = query.split('&')
  const result = {}
  for (let i = 0; i < vars.length; i++) {
    const pair = vars[i].split('=')
    const key = decodeURIComponent(pair[0])
    if (key.startsWith(variable)) {
      result[key] = decodeURIComponent(pair[1])
    }
  }
  return result
}

function getUtms() {
  const utms = location.search
    .replace('?', '')
    .split('&')
    .reduce((p, e) => {
      const a = e.split('=')
      p[decodeURIComponent(a[0]).toLowerCase()] = decodeURIComponent(a[1])
      return p
    }, {})

  return utms
}

function removeUtms() {
  if (history.pushState) {
    let keep = location.search
      .replace('?', '')
      .split('&')
      .filter((k) => !k.startsWith('utm_'))
      .join('&')

    if (keep.length > 0) {
      keep = `?${keep}`
    }

    const newUrl = `${location.href.split('?')[0]}${keep}`

    history.pushState(null, '', newUrl)
  }
}

function processUTMs(onClick: (href: string, e: Event) => void) {
  try {
    const host = location.href
      .match(/^https?:\/\/([^/]+)/i)[1]
      .replace(/^www./, '')

    const utms = createUtmsparams(getUtms())

    // Find all links in current document
    const links = document.getElementsByTagName('a')

    for (let i = 0; i < links.length; i++) {
      const { href } = links[i]

      const isExternal =
        href &&
        !href.match(/^javascript:/i) &&
        href.indexOf(host) === -1 &&
        href.match(/^https?:\/\//i)

      if (isExternal) {
        if (links[i].addEventListener) {
          links[i].addEventListener('click', (e) => onClick(href, e))
        } else {
          // eslint-disable-next-line dot-notation
          links[i]['attachEvent']('onclick', (e) => onClick(href, e))
        }
      } else if (utms && href && href.indexOf(host) !== -1) {
        // updateLocalLinks if needed, not implemented for React SPA
      }
    }
  } catch (e) {
    console.log(e)
  }
}

function createUtmsparams(utms: Utms) {
  const checkObj = [
    'utm_campaign',
    'utm_source',
    'utm_medium',
    'utm_content',
    'utm_term',
    'utm_cid'
  ]
  const selfUtms = []
  Object.keys(utms).forEach((k) => {
    if (checkObj.includes(k)) {
      selfUtms.push(`${encodeURIComponent(k)}=${encodeURIComponent(utms[k])}`)
    }
  })
  return selfUtms.join('&')
}
